import { Component, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { cloneDeep } from "lodash";
import { ContentTextEditModel, ContentTextModel } from "@/services/content";
import { fmtMsg, MessageHelper } from "commonui";
import { EmptyGuid } from "@/util/constants";
import { TitleListLocale } from "@/locales/localeid";
import { Route } from "vue-router";
import { createTextMap } from "@/util/func";
import { ApplicationViews } from "@/util/enum";
import AppSettings from "@/modules/app-settings";
import TextTagEditor from "@/components/text-tag-editor/text-tag-editor.vue";
import { getEmptyTextLine } from "./text-helper";

@Component({
	components: { TextTagEditor }
})
export default class TextList extends Vue {
	private titleListModule = getModule(TitleListState);
	public currentTextDetails: ContentTextEditModel[] = [];
	public initialTextDetails: ContentTextModel[] = [];
	private editedContentTextIdMap: Map<string, number> = new Map(); // to map with temporary guid and actual id of text for internal use of this component (see processdetails)
	public appSetting = getModule(AppSettings) as AppSettings;
	public editorRef = "ediRef";
	public contentName = "";

	created() {
		this.processDetails();
	}

	beforeRouteLeave(to: Route, from: Route, next: Function) {
		const { editedContentId, formTouched, textPageDirty, savingTitle, isParent } = this.titleListModule;
		const isDirty = editedContentId.length
			? textPageDirty || formTouched
			: (isParent && textPageDirty) || formTouched;

		const updateChapterId = () => {
			const {
				possibleEditeContentSelectedChapterId,
				setEditeContentSelectedChapterId,
				setPossibleEditeContentSelectedChapterId
			} = this.titleListModule;
			if (possibleEditeContentSelectedChapterId.length) {
				setEditeContentSelectedChapterId(possibleEditeContentSelectedChapterId);
				setPossibleEditeContentSelectedChapterId("");
			}
		};

		if (isDirty && !savingTitle) {
			MessageHelper.Confirm(
				fmtMsg(TitleListLocale.TitleListLeaveWarn),
				() => {
					if (next) {
						// Update the chapterId only when user confirms to leave
						// It would happen in case of 'all'
						updateChapterId();

						// If the user leaves, reset default state.
						this.resetToDefaults();
						next();
					}
				},
				() => {
					this.appSetting.setChildMenu(ApplicationViews.Text);
					// reset possible chapter id
					const {
						possibleEditeContentSelectedChapterId,
						setPossibleEditeContentSelectedChapterId
					} = this.titleListModule;

					if (possibleEditeContentSelectedChapterId.length) {
						setPossibleEditeContentSelectedChapterId("");
					}
					next(false);
				}
			);
		} else {
			// if not dirty then update the chapter Id
			updateChapterId();
			next();
		}
	}

	@Watch("titleListModule.editedContentDetails.id")
	@Watch("titleListModule.editeContentSelectedChapterId")
	async reProcess() {
		this.processDetails();
	}

	@Watch("titleListModule.revisionChanged")
	async revisionChanged() {
		if (!this.titleListModule.revisionChanged) {
			return;
		}
		while (this.titleListModule.loading) {
			await new Promise((res) => setTimeout(res, 250));
		}
		this.processDetails();
		this.titleListModule.setRevisionChanged(false);
	}

	// runs when user comes back from comparer
	@Watch("titleListModule.forceProcess", { immediate: true })
	async forceProcess() {
		if (!this.titleListModule.forceProcess) {
			return;
		}
		while (this.titleListModule.loading) {
			await new Promise((res) => setTimeout(res, 250));
		}
		this.processDetails();
		this.titleListModule.setForceProcess(false);
	}

	processDetails() {
		const {
			editedContentDetails,
			editedContentId,
			editeContentSelectedChapterId,
			currentRevision
		} = this.titleListModule;
		this.setContentName();

		if (editedContentDetails.id && editedContentId.length) {
			// Edit Mode
			let text: ContentTextModel[] = [];
			if (editedContentDetails.chapters.length) {
				// edited title have chapters
				if (editeContentSelectedChapterId === "none") {
					return;
				} else {
					text = editedContentDetails.chapters
						.find((chap) => chap.id === editeContentSelectedChapterId)
						?.contentText.filter(
							(c) => c.revision === (currentRevision === -1 ? 0 : currentRevision)
						) as ContentTextModel[];
					if (!text || !text.length) {
						text = [getEmptyTextLine(editeContentSelectedChapterId)] as ContentTextEditModel[];
					}
				}
			} else {
				const contentdetails = editedContentDetails;
				text =
					contentdetails.contentText && contentdetails.contentText.length
						? contentdetails.contentText
						: ([
								getEmptyTextLine(
									this.titleListModule.editedContentId.length
										? this.titleListModule.editedContentId
										: EmptyGuid
								)
						  ] as ContentTextEditModel[]);
			}
			this.initialTextDetails = cloneDeep(text);
			const replacedIdText = this.replaceIntIdWithString(cloneDeep(text));
			this.currentTextDetails = replacedIdText.map((item) => {
				const editItem = item as ContentTextEditModel;
				editItem.previousText = item.textContent;
				return editItem;
			});
		} else {
			// create Mode
			const details = [
				getEmptyTextLine(
					this.titleListModule.editedContentId.length ? this.titleListModule.editedContentId : EmptyGuid
				)
			];
			this.currentTextDetails = details;
			this.titleListModule.setValidText(true);
		}
	}

	replaceIntIdWithString(textDetails: ContentTextModel[]) {
		// Replace contentText integer id with Guid, and replace with integer when calling API
		const { details, map } = createTextMap(textDetails);
		this.editedContentTextIdMap = map;
		return details;
	}

	setContentName() {
		const { editeContentSelectedChapterId, editedContentDetails } = this.titleListModule;
		this.contentName =
			editeContentSelectedChapterId !== "none" && editeContentSelectedChapterId !== "all"
				? `${
						editedContentDetails.chapters.find((c) => c.id === editeContentSelectedChapterId)
							?.title as string
				  } - ${editedContentDetails.title}`
				: editedContentDetails.title;
	}

	get isChapterSelected() {
		const { editedContentDetails, editeContentSelectedChapterId } = this.titleListModule;
		return (
			editedContentDetails.id &&
			editedContentDetails.chapters.length > 0 &&
			editeContentSelectedChapterId === "none"
		);
	}

	toggleDirty() {
		if (!this.titleListModule.textPageDirty) {
			this.titleListModule.togglePageDirty();
		}
	}

	textValidated(isValid: boolean) {
		this.titleListModule.setValidText(isValid);
	}

	getTextDetails(): boolean | { textDetails: ContentTextEditModel[]; initialTextDetails: ContentTextModel[] } {
		const editorRef: any = this.$refs[this.editorRef];
		const updatedDetails = editorRef.updatedTextDetails();
		if (updatedDetails && Array.isArray(updatedDetails)) {
			return {
				initialTextDetails: this.initialTextDetails,
				textDetails: updatedDetails.map((text) => {
					text.id = this.editedContentTextIdMap.get(text.id as string) as any;
					text.textContent = text.textContent.trim();
					return text;
				})
			};
		}
		return false;
	}

	resetToDefaults() {
		if (this.titleListModule.textPageDirty) {
			this.titleListModule.togglePageDirty();
		}
		if (!this.titleListModule.isParent && this.titleListModule.checkIsParent) {
			this.titleListModule.setCheckboxParent(false);
		}
	}
}
